import { FullPageLoader } from "@/components/FullPageLoader";
import { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invokeFunction } from "@/lib/invokeFunction";
import { Session, AuthChangeEvent } from "@supabase/supabase-js";
import { toast } from "sonner";
import { useUserSession } from "@/hooks/use-user-session";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const hasTrackedLogin = useRef(false);
  const { data: userSession, isLoading: sessionLoading } = useUserSession();

  useEffect(() => {
    const handleAuthChange = (event: AuthChangeEvent, session: Session | null) => {
      // Suppress cross-tab SIGNED_OUT broadcasts: Supabase fires SIGNED_OUT on
      // every tab when any tab signs out. If this tab still has a live session
      // in storage and did not itself initiate the sign-out, ignore it.
      if (event === "SIGNED_OUT") {
        try {
          const w = window as unknown as { __lovableLocalSignOut?: boolean };
          const keys = Object.keys(localStorage);
          const hasStoredSession = keys.some(
            (k) => k.startsWith("sb-") && k.endsWith("-auth-token") && !!localStorage.getItem(k),
          );
          if (hasStoredSession && !w.__lovableLocalSignOut) {
            return;
          }
        } catch {
          // fall through to default behaviour
        }
      }

      setSession(session);
      setLoading(false);

      if (session?.user && !hasTrackedLogin.current) {
        hasTrackedLogin.current = true;
        // Fire and forget - don't await
        trackUserLogin();
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthChange);

    // THEN check for existing session — use getSession (cached, no network)
    // to avoid an extra /auth/v1/user round-trip on every route enter.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        if (session && !hasTrackedLogin.current) {
          hasTrackedLogin.current = true;
          trackUserLogin();
        }
        setLoading(false);
      })
      .catch((error) => {
        // getSession() is local/synchronous — a rejection means corrupt
        // storage or a JSON parse error, not an auth failure. Do NOT force
        // sign-out; render as unauthenticated for this pass and let
        // onAuthStateChange recover on next tick.
        console.error("Error reading local auth session:", error);
        setSession(null);
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, []);

  const trackUserLogin = () => {
    // Deduplicate across navigations within the same session using sessionStorage
    const todayKey = `login_tracked_${new Date().toISOString().split('T')[0]}`;
    if (sessionStorage.getItem(todayKey)) return;
    sessionStorage.setItem(todayKey, '1');

    // Defer to after first paint so it never competes with the login-critical
    // path. requestIdleCallback where available; setTimeout fallback (3s).
    const dispatch = () => {
      invokeFunction('track-user-login').catch((error) => {
        console.error('Error tracking login:', error);
      });
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(dispatch, { timeout: 3000 });
    } else {
      setTimeout(dispatch, 3000);
    }
  };

  // Derive profile-completion gating from the cached user-session query.
  const roles = userSession?.roles ?? [];
  const hasParentOrTutorRole = roles.some((r) => r === "parent" || r === "tutor");
  const isTutor = roles.includes("tutor");
  const profileIncomplete =
    !!userSession?.userId &&
    hasParentOrTutorRole &&
    userSession.profileCompleted === false &&
    !!userSession.userType;

  if (loading) {
    return <FullPageLoader message="Loading..." />;
  }

  if (!session) {
    const loginPath = location.pathname.startsWith("/app") ? "/app/login" : "/login";
    return <Navigate to={loginPath} replace />;
  }

  // While the shared session query is still loading we don't yet know whether
  // the profile is complete — render children rather than blocking the page.
  if (!sessionLoading && profileIncomplete) {
    if (isTutor && location.pathname !== "/tutor-profile") {
      return <Navigate to="/tutor-profile" replace />;
    }
    if (!isTutor && location.pathname !== "/complete-profile") {
      return <Navigate to="/complete-profile" replace />;
    }
  }

  return <>{children}</>;
}
